import {otsBaseApi} from "../../ots-api";
import {ServiceEventTypeServiceType} from "../../../model/service/service-event-type-service-type";
import {removeUndefined} from "../../../util/util";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: [
    "ServiceEventTypeServiceTypes",
    "ServiceEventTypeServiceTypesByType",
    "ServiceEventTypeServiceTypesByService",
    "ObjectObjectChangeLogs"
  ]
});

const serviceEventApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllServiceEventTypeServiceTypes: builder.query<ServiceEventTypeServiceType[], string>({
      query: (regionId) => ({
        url: `/service-event-type-service-types/all`,
        method: "GET",
        params: removeUndefined({
          regionId: regionId
        })
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "ServiceEventTypeServiceTypes", id} as const)),
            {type: "ServiceEventTypeServiceTypes", id: "LIST"}
          ];
        } else {
          return [{type: "ServiceEventTypeServiceTypes", id: "LIST"}];
        }
      }
    }),
    getAllServiceEventTypeServiceTypesByEventType: builder.query<ServiceEventTypeServiceType[], {
      eventType: string
      companyId: string
      regionId?: string
    }>({
      query: ({eventType, companyId, regionId}) => ({
        url: `/service-event-type-service-types/event-type/${eventType}`,
        method: "GET",
        params: removeUndefined({
          companyId: companyId,
          regionId: regionId
        })
      }),
      providesTags: (result, error, {eventType, companyId}) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "ServiceEventTypeServiceTypes", id} as const)),
            {type: 'ServiceEventTypeServiceTypesByType', id: eventType + ":" + companyId},
          ];
        } else {
          return [{type: "ServiceEventTypeServiceTypesByType", id: eventType + ":" + companyId}];
        }
      }
    }),
    getAllServiceEventTypeServiceTypesByServiceType: builder.query<ServiceEventTypeServiceType[], {
      serviceTypeId: string
      companyId: string
      regionId?: string
    }>({
      query: ({serviceTypeId, companyId, regionId}) => ({
        url: `/service-event-type-service-types/service-type/${serviceTypeId}`,
        method: "GET",
        params: removeUndefined({
          companyId: companyId,
          regionId: regionId
        })
      }),
      providesTags: (result, error, {serviceTypeId, companyId}) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "ServiceEventTypeServiceTypes", id} as const)),
            {type: 'ServiceEventTypeServiceTypesByService', id: serviceTypeId + ":" + companyId},
          ];
        } else {
          return [{type: "ServiceEventTypeServiceTypesByService", id: serviceTypeId + ":" + companyId}];
        }
      }
    }),
    getServiceEventTypeServiceType: builder.query<ServiceEventTypeServiceType, string>({
      query: (id) => ({
        url: `/service-event-type-service-types/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "ServiceEventTypeServiceTypes", id}]
    }),
    createServiceEventTypeServiceType: builder.mutation<ServiceEventTypeServiceType, Partial<ServiceEventTypeServiceType>>({
      query: (mapping) => ({
        url: "/service-event-type-service-types",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [
        {type: "ServiceEventTypeServiceTypes", id: "LIST"},
        {type: "ServiceEventTypeServiceTypesByType"},
        {type: "ServiceEventTypeServiceTypesByService"}
      ]
    }),
    updateServiceEventTypeServiceType: builder.mutation<ServiceEventTypeServiceType, Partial<ServiceEventTypeServiceType>>({
      query: (mapping) => ({
        url: "/service-event-type-service-types",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "ServiceEventTypeServiceTypes", id},
        {type: "ObjectObjectChangeLogs", id},
        {type: "ServiceEventTypeServiceTypesByType"},
        {type: "ServiceEventTypeServiceTypesByService"}
      ]
    }),
    deleteServiceEventTypeServiceType: builder.mutation<void, Partial<ServiceEventTypeServiceType>>({
      query: (mapping) => ({
        url: "/service-event-type-service-types",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "ServiceEventTypeServiceTypes", id},
        {type: "ServiceEventTypeServiceTypesByType"},
        {type: "ServiceEventTypeServiceTypesByService"}
      ]
    }),
  })
});

export const {
  useGetServiceEventTypeServiceTypeQuery,
  useGetAllServiceEventTypeServiceTypesQuery,
  useGetAllServiceEventTypeServiceTypesByEventTypeQuery,
  useGetAllServiceEventTypeServiceTypesByServiceTypeQuery,
  useCreateServiceEventTypeServiceTypeMutation,
  useUpdateServiceEventTypeServiceTypeMutation,
  useDeleteServiceEventTypeServiceTypeMutation,
} = serviceEventApi;
